import React, { useCallback, useEffect, useState } from 'react';
import { NavLink, Outlet, Routes, Route, Navigate, useOutletContext } from 'react-router-dom';
import Login from '../../../components/Login';
import { logout, getToken } from '../../../utils/auth';
import { isMockMode, hasTradingToken } from '../../../services/stockai/api/client';
import Dashboard from '../Dashboard';
import Trade from '../Trade';
import Portfolio from '../Portfolio';
import AI from '../AI';
import Intro from '../Intro';
import Settings from '../Settings';
import '../../../styles/stockai-design-system.css';
import './StockAILayout.css';

const STOCKAI_PREFIX = process.env.REACT_APP_STOCKAI_ONLY === '1' ? '' : '/stockai';

const NAV = [
  { to: `${STOCKAI_PREFIX}/intro`, label: '소개', icon: '✨' },
  { to: `${STOCKAI_PREFIX}/dashboard`, label: '대시보드', icon: '📊' },
  { to: `${STOCKAI_PREFIX}/trade`, label: '거래', icon: '📈' },
  { to: `${STOCKAI_PREFIX}/portfolio`, label: '포트폴리오', icon: '💼' },
  { to: `${STOCKAI_PREFIX}/ai`, label: 'AI', icon: '🤖' },
  { to: `${STOCKAI_PREFIX}/settings`, label: '설정', icon: '⚙️' },
];

export default function StockAILayout() {
  const [showLogin, setShowLogin] = useState(false);
  const [loggedIn, setLoggedIn] = useState(() => hasTradingToken());
  // 로그인 시 목업 해제 → 실 시세·실 주문. 미로그인 + 목업 빌드만 mock.
  const mock = !loggedIn && isMockMode();

  const [authTick, setAuthTick] = useState(0);

  const refreshAuth = useCallback(() => {
    setLoggedIn(!!getToken());
  }, []);

  useEffect(() => {
    refreshAuth();
  }, [refreshAuth]);

  const handleLoginSuccess = (_user, _token) => {
    setLoggedIn(!!getToken());
    setShowLogin(false);
    setAuthTick((n) => n + 1);
    window.dispatchEvent(new Event('stockai-auth-changed'));
  };

  const handleLogout = () => {
    logout();
    setLoggedIn(false);
    setAuthTick((n) => n + 1);
    // 로그아웃 후 목업 가능 빌드면 로그인 강제하지 않음
    if (process.env.REACT_APP_USE_MOCK === 'false') setShowLogin(true);
  };

  return (
    <div className="stockai-app stockai-layout">
      <header className="stockai-layout__header">
        <div className="stockai-layout__brand">
          <span className="stockai-layout__logo">제우스</span>
          <span className="stockai-layout__subtitle">StockAI Trading Studio</span>
          {loggedIn ? (
            <span className="stockai-layout__badge stockai-layout__badge--live">LIVE</span>
          ) : mock ? (
            <span className="stockai-layout__badge">MOCK</span>
          ) : null}
        </div>
        <div className="stockai-layout__actions">
          {loggedIn ? (
            <>
              <span className="stockai-layout__user">실실행 연결</span>
              <button type="button" className="stockai-btn stockai-btn--ghost stockai-btn--sm" onClick={handleLogout}>
                로그아웃
              </button>
            </>
          ) : (
            <button type="button" className="stockai-btn stockai-btn--sm" onClick={() => setShowLogin(true)}>
              로그인
            </button>
          )}
          <a className="stockai-layout__legacy" href="/trading/">기존 Trading</a>
        </div>
      </header>

      <div className="stockai-layout__body">
        <nav className="stockai-layout__sidebar" aria-label="StockAI 메뉴">
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) =>
                `stockai-layout__nav-link${isActive ? ' stockai-layout__nav-link--active' : ''}`
              }
            >
              <span>{item.icon}</span> {item.label}
            </NavLink>
          ))}
        </nav>

        <main className="stockai-layout__main">
          <Outlet context={{ loggedIn, mock, openLogin: () => setShowLogin(true), authTick }} />
        </main>
      </div>

      <nav className="stockai-layout__mobile-nav" aria-label="모바일 메뉴">
        {NAV.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            className={({ isActive }) =>
              `stockai-layout__mobile-link${isActive ? ' stockai-layout__mobile-link--active' : ''}`
            }
          >
            <span>{item.icon}</span>
            <small>{item.label}</small>
          </NavLink>
        ))}
      </nav>

      {showLogin && (
        <Login
          onLoginSuccess={handleLoginSuccess}
          onClose={() => setShowLogin(false)}
        />
      )}
    </div>
  );
}

function StockAIAppShell({ children }) {
  useEffect(() => {
    document.body.classList.add('stockai-body');
    document.body.classList.remove('site-dark');
    // StrictMode cleanup에서 body 클래스가 제거되지 않도록 유지
  }, []);
  return children;
}

export function StockAIRoutes() {
  const stockaiOnly = process.env.REACT_APP_STOCKAI_ONLY === '1';

  const childRoutes = (
    <>
      <Route index element={<Navigate to="intro" replace />} />
      <Route path="intro" element={<Intro />} />
      <Route path="dashboard" element={<Dashboard />} />
      <Route path="trade" element={<Trade />} />
      <Route path="portfolio" element={<Portfolio />} />
      <Route path="ai" element={<AI />} />
      <Route path="settings" element={<Settings />} />
      <Route path="*" element={<Navigate to="intro" replace />} />
    </>
  );

  return (
    <StockAIAppShell>
      <Routes>
        {stockaiOnly ? (
          <Route element={<StockAILayout />}>{childRoutes}</Route>
        ) : (
          <Route path="/stockai" element={<StockAILayout />}>{childRoutes}</Route>
        )}
      </Routes>
    </StockAIAppShell>
  );
}

export function mountStockAI() {
  const React = require('react');
  const ReactDOM = require('react-dom/client');
  const reportWebVitals = require('../../../reportWebVitals').default;
  const StockAIEntry = require('../../StockAIEntry').default;
  require('../../../styles/stockai-design-system.css');
  document.body.classList.add('stockai-body');
  document.body.classList.remove('site-dark');
  document.title = '제우스 StockAI';
  const root = ReactDOM.createRoot(document.getElementById('root'));
  root.render(
    React.createElement(React.StrictMode, null, React.createElement(StockAIEntry))
  );
  reportWebVitals();
}
